import { ApiError } from './api-error';
import { toRefusal, type Refusal } from './refusal';
/**
 * A SESSION CHANGE THE SERVER DID NOT MAKE IS SAID (VC-Authz 0b74f9498c,
 * 2026-10-26): switching organisation, admin mode and its write mode,
 * impersonation and its write mode used to act only on an OK answer and
 * drop every refusal without a word. Now the answer is read as the one
 * refusal and shown at the top of the page.
 *
 * `session_moved_on` (409, `next: ["reload"]`): the change was made on a
 * stale cookie — another tab changed the session, or the last change's
 * answer never reached this browser. The refusal carries the caught-up
 * cookie, so the page reloads once, by itself; its reason is kept in
 * sessionStorage over the reload and shown on the new page.
 */

export const SESSION_MOVED_ON = 'session_moved_on';
const KEY = 'sp-session-change-refused';

export type SessionChangeRefusal = { code: string; reason: string };

let current: SessionChangeRefusal | null = null;
const listeners = new Set<() => void>();
let reloadPage = () => window.location.reload();
let carried = false;

function say(r: SessionChangeRefusal | null): void {
  current = r;
  for (const l of Array.from(listeners)) l();
}

/** a refusal said before the last reload, once */
function carriedOver(): void {
  if (carried) return;
  carried = true;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    sessionStorage.removeItem(KEY);
    const r = JSON.parse(raw) as SessionChangeRefusal;
    if (r && typeof r.reason === 'string') current = { code: String(r.code ?? ''), reason: r.reason };
  } catch { /* no storage: nothing carried */ }
}

/** The refusal shown now, if any. */
export function sessionChangeRefusal(): SessionChangeRefusal | null {
  carriedOver();
  return current;
}

export function onSessionChangeRefusal(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function dismissSessionChangeRefusal(): void {
  say(null);
}

/** A non-OK answer to a session change: read, said, and for
 *  `session_moved_on` the page reloaded once. Answers the refusal. */
export async function sessionChangeRefused(response: Response, fallback: string, reload: () => void = reloadPage): Promise<Refusal> {
  let text = '';
  try { text = await response.text(); } catch { /* no body */ }
  let body: unknown = text;
  try { body = text ? JSON.parse(text) : null; } catch { /* raw text */ }
  const refusal = toRefusal(new ApiError(response.status, body, text), fallback);
  const shown = { code: refusal.code, reason: refusal.reason };
  say(shown);
  if (refusal.code === SESSION_MOVED_ON) {
    try { sessionStorage.setItem(KEY, JSON.stringify(shown)); } catch { /* said on this page only */ }
    reload();
  }
  return refusal;
}

/** No answer at all: said, never only logged. */
export function sessionChangeFailed(error: unknown, fallback: string): Refusal {
  const refusal = toRefusal(error, fallback);
  say({ code: refusal.code, reason: refusal.reason });
  return refusal;
}

/** tests only: a fresh page, reloading with `reload` */
export function resetSessionChangeRefusalForTests(reload: () => void = () => {}): void {
  current = null;
  carried = false;
  reloadPage = reload;
  listeners.clear();
}
